import { useRef, useEffect, useState, useCallback } from "react";
import { invalidate, ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import { useFloorplannerStore, floorplannerStore } from '../../store/floorplannerStore';
import {
  Room,
} from "../../types/floorplannerTypes";
import { observer } from "mobx-react-lite";
import { WallShape } from "./WallShape";
import GeometryErrorBoundary from "./GeometryErrorBoundary";
import { renderStore } from "../../store/renderStore";

const WallStructure = observer(() => {
  const floorplannerStore = useFloorplannerStore();
  const {
    wallsMap,
  } = floorplannerStore;
  const { gl } = useThree();

  useEffect(() => {
    const handleContextLost = (event: Event) => {
      event.preventDefault();
      console.warn("WebGL context lost");
    };

    const handleContextRestored = () => {
      console.log("WebGL context restored");
      invalidate();
    };

    gl.domElement.addEventListener(
      "webglcontextlost",
      handleContextLost as EventListener,
    );
    gl.domElement.addEventListener(
      "webglcontextrestored",
      handleContextRestored as EventListener,
    );

    return () => {
      gl.domElement.removeEventListener(
        "webglcontextlost",
        handleContextLost as EventListener,
      );
      gl.domElement.removeEventListener(
        "webglcontextrestored",
        handleContextRestored as EventListener,
      );
    };
  }, [gl.domElement]);

  const handleRecoverPolygon = useCallback((wallId: string) => {
    renderStore.attemptErrorRecovery(wallId, 'composition');
  }, []);

  const handleRecoverTrim = useCallback((wallId: string) => {
    renderStore.attemptErrorRecovery(wallId, 'trimming');
  }, []);

  const handleRecoverComposition = useCallback((wallId: string) => {
    renderStore.attemptErrorRecovery(wallId, 'composition');
  }, []);

  const handleRecoverMemory = useCallback(() => {
    renderStore.clearGeometryCache?.();
  }, []);

  const handleRecoverGeneric = useCallback((wallId: string, operation: string) => {
    renderStore.attemptErrorRecovery(wallId, operation);
  }, []);

  const handleError = useCallback((error: Error, errorInfo: React.ErrorInfo) => {
    renderStore.handleGeometryError('geometry_operation', 'unknown', error);
  }, []);

  const handleRecovery = useCallback((recoveryType: string) => {
    console.log('WallStructure: Geometry recovery completed', { recoveryType });
  }, []);

  return (
    <>
      {/* Render walls with error boundary */}
      {Array.from(wallsMap.values()).map((wall) => (
        <GeometryErrorBoundary
          key={wall.id}
          wallId={wall.id}
          operationType="wall_rendering"
          onError={handleError}
          onRecovery={handleRecovery}
          onRecoverPolygon={handleRecoverPolygon}
          onRecoverTrim={handleRecoverTrim}
          onRecoverComposition={handleRecoverComposition}
          onRecoverMemory={handleRecoverMemory}
          onRecoverGeneric={handleRecoverGeneric}
          maxRecoveryAttempts={3}
          enableDevelopmentMode={process.env.NODE_ENV === 'development'}
        >
          <WallShape
            wall={wall}
          />
        </GeometryErrorBoundary>
      ))}
    </>
  );
});

export default WallStructure;